import "styles/staff/staff_cards.css";

import {
    CaretDownFilled,
    CheckSquareFilled,
    FilterFilled
} from "@ant-design/icons";
import { Box } from "@chakra-ui/react";
import { Button, Col, Dropdown, Menu, Row, Select, Space } from "antd";
import Search from "antd/lib/input/Search";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import Table, { ColumnsType } from "antd/lib/table";
import { FilterValue } from "antd/lib/table/interface";
import Currency from "components/Currency";
import { AccountSearch, BatchSearch, PackageTypeSearch } from "components/EntityTableSearch";
import MultiValueDisplay from "components/MultiValueDisplay";
import OwnersDisplay from "components/OwnersDisplay";
import TableActions from "components/TableActions";
import {
    BulkEditFilterModal,
    BulkEditSelectionModal,
} from "components/staff/Cards/BulkEditModals";
import CardActions from "components/staff/Cards/CardActions";
import CardOwnership from "components/staff/Cards/CardOwnership";
import CardTitleSearch from "components/staff/Cards/CardTitleSearch";
import NewListingModal from "components/staff/Cards/NewListingModal";
import StaffCardsFilter, {
    StaffCardFilterDetail,
} from "components/staff/Cards/StaffCardsFilter";
import StatusFilter from "components/staff/Cards/StatusFilter";
import FriendlyIdFilter from "components/staff/Packages/FriendlyIdFilter";
import { Roles, useIdentity } from "context/auth";
import useStaffCards, { useStaffCardsCSV } from "hooks/useStaffCards";
import { StaffSportsCard } from "models/cards";
import { InitialPageRequest, PageRequest, PageSizeOptions } from "models/common";
import React, { useState } from "react";
import downloadAsCSV from "util/downloadAsCSV";
import { localizedDate } from "util/helpers";
import { createCustomFilter } from "util/table";

enum FindByKey {
    Unknown = 0,
    eBayItemId = 1,
    SKU = 2
}

const invalidOwnershipBy = (type: "Selection" | "Filter") => (
    {
        label: `🚫 ${type} Includes Cards With Mismatched Ownership Types`,
        key: `no-bulk-edit-by-${type.toLocaleLowerCase()}`,
        disabled: true
    }
);

const Cards = () => {
    const [{ page, filterBy, withinDays, sort, pageSize }, setPageRequest] =
        useState<PageRequest>(InitialPageRequest);
    const { isLoading, data, refetch } = useStaffCards({
        page,
        filterBy,
        withinDays,
        sort,
        pageSize
    });
    const { isInRole } = useIdentity();
    const [selectedCardIds, setSelectedCardIds] = useState<React.Key[]>([]);
    const [filterDetail, setFilterDetail] = useState<StaffCardFilterDetail>({});
    const [recentUpdates, setRecentUpdates] = useState<Set<string>>(new Set());
    const [bulkSelectionModalVisible, setBulkSelectionModalVisible] =
        useState<boolean>(false);
    const [bulkFilterModalVisible, setBulkFilterModalVisible] =
        useState<boolean>(false);
    const [canBulkEditSelection, setCanBulkEditSelection] = useState(false);
    const [findByKey, setFindByKey] = useState(FindByKey.eBayItemId);

    const { data: records = [], totalRecords } = data || {};
    const isAdminOrSeniorStaff = isInRole([Roles.Admin, Roles.SeniorStaff]);

    const setTablePage = (pageRequest: PageRequest) => {
        setPageRequest(pageRequest);
        setRecentUpdates(new Set());
    };

    const daysChanged = (value: number) => {
        setTablePage({ page: 1, filterBy, withinDays: value, sort, pageSize });
    };

    const tableChanged = (
        // pagination
        { current: newPage, pageSize: newPageSize }: any,
        // filtering
        filters: Record<string, FilterValue | null>,
        // sorting
        { column, field, order }: any
    ) => {
        setTablePage({
            page: newPageSize !== pageSize ? 1 : newPage,
            pageSize: newPageSize,
            filterBy: filters,
            withinDays,
            sort: column ? [field, order === "ascend"] : null,
        });
    };

    const highlightAndRefresh = async (updatedCardIds: string[]) => {
        setRecentUpdates(() => new Set(updatedCardIds));
        await refetch();
    };

    const onFilterUpdate = (key: string) => (value: any) => {
        setFilterDetail((prev) => ({ ...prev, [key]: value }));
    };

    const isFiltering = (): boolean => {
        return (
            records !== undefined &&
            records.length > 0 &&
            (filterDetail.batch !== undefined ||
                filterDetail.account !== undefined ||
                filterDetail.status !== undefined ||
                filterDetail.friendlyPackageId !== undefined ||
                filterDetail.title !== undefined)
        );
    };

    const allSameOwnershipModel = (cards: StaffSportsCard[]): boolean => {
        if (cards.length === 0) {
            return false;
        }

        const sameSilentAccountSupport = cards.every(c => c.ownersAreSilent === cards[0].ownersAreSilent);
        const sameMultipleOwnerSupport = cards.every(c => c.canHaveMultipleOwners === cards[0].canHaveMultipleOwners);

        return sameSilentAccountSupport && sameMultipleOwnerSupport;
    }

    const getBulkMenu = () => {
        const items: ItemType[] = [];

        if (selectedCardIds.length > 0 && !canBulkEditSelection) {
            items.push(invalidOwnershipBy("Selection"));
        } else {
            items.push({
                label: "All Selected",
                key: "by-selection",
                icon: <CheckSquareFilled />,
                disabled: selectedCardIds.length < 2,
                onClick: () => setBulkSelectionModalVisible(true),
            });
        }

        if (isFiltering() && !allSameOwnershipModel(records)) {
            items.push(invalidOwnershipBy("Filter"));
        } else {
            items.push({
                label: "All Filtered",
                key: "by-filtered",
                icon: <FilterFilled />,
                disabled: !isFiltering(),
                onClick: () => setBulkFilterModalVisible(true),
            });
        }

        return <Menu items={items} />
    }

    const columns: ColumnsType<StaffSportsCard> = [
        {
            title: "Batch",
            dataIndex: "batchNumber",
            key: "batchId",
            sorter: true,
            ...createCustomFilter(
                BatchSearch,
                onFilterUpdate("batch"),
                filterDetail.batch?.id
            ),
            render: (bn, rec) => (
                <MultiValueDisplay
                    title={bn}
                    subTitle={localizedDate(rec.batchCreatedDate)}
                />
            ),
        },
        {
            title: "Package",
            dataIndex: "friendlyPackageId",
            key: "friendlyPackageId",
            sorter: false,
            ...createCustomFilter(
                FriendlyIdFilter,
                onFilterUpdate("friendlyPackageId"),
                filterDetail.friendlyPackageId
            ),
            render: (id, rec) => <Space direction="vertical">
                {(id === 0 ? "N/A" : id)}
                <CardOwnership {...rec} />
            </Space>,
        },
        {
            title: "Package Type",
            dataIndex: "packageTypeName",
            key: "packageTypeId",
            sorter: false,
            ...createCustomFilter(
                PackageTypeSearch,
                onFilterUpdate("packageType"),
                filterDetail.packageType?.id
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            ...createCustomFilter(
                StatusFilter,
                onFilterUpdate("status"),
                filterDetail.status
            ),
        },
        {
            title: <MultiValueDisplay title="Card Title" subTitle="eBay Item ID" titleColor="inherit" subTitleColor="inherit" />,
            dataIndex: "title",
            key: "title",
            sorter: true,
            ...createCustomFilter(
                CardTitleSearch,
                onFilterUpdate("title"),
                filterDetail.title
            ),
            render: (n, rec) => (
                <MultiValueDisplay
                    title={n}
                    subTitle={rec.ebayItemId}
                />
            ),
        },
        {
            title: "Customer(s)",
            dataIndex: "accountId",
            key: "accountId",
            sorter: false,
            ...createCustomFilter(
                AccountSearch,
                onFilterUpdate("account"),
                filterDetail.account?.id
            ),
            render: (_, rec) => <OwnersDisplay owners={rec.owners} />,
        },
        {
            title: "Listing Date",
            dataIndex: "listingDate",
            key: "listingDate",
            sorter: true,
            render: localizedDate,
        },
        {
            title: "Sale Price",
            dataIndex: "salePriceInCents",
            key: "salePrice",
            sorter: true,
            align: "right",
            render: (a) => <Currency cents={a} />,
        },
        {
            title: "",
            key: "edit",
            render: (_, rec) => <CardActions
                card={rec}
                onUpdate={async (cardId) => await highlightAndRefresh([cardId])}
            />,
        },
    ];

    const fetchTransactionsCSV = useStaffCardsCSV({
        filterBy,
        withinDays,
        sort,
    });

    const handleCSVExport = async () => {
        const csvData = await fetchTransactionsCSV();
        const date = new Date().toISOString().split("T")[0];
        const filename = `all-cards-${date}.csv`;
        downloadAsCSV(csvData, filename);
    };

    const handleFindBy = (value: string) => {
        const pageRequest: PageRequest = { page: 1, pageSize, withinDays, sort };

        if (value.trim() === '') {
            const { EbayItemId, SKU, ...rest } = filterBy as any;
            pageRequest["filterBy"] = { ...rest };
        } else {
            switch (findByKey) {
                case FindByKey.eBayItemId:
                    pageRequest["filterBy"] = { ...filterBy, EbayItemId: [value] };
                    break;
                case FindByKey.SKU:
                    pageRequest["filterBy"] = { ...filterBy, SKU: [value] };
                    break;
            }
        }

        setTablePage(pageRequest);
    }

    return (
        <Box p={50}>
            <Row align="bottom">
                <Col span={6}>
                    <Box fontSize={36}>All Cards</Box>
                </Col>
                <Col span={10} offset={8} className="actions-container">
                    <TableActions
                        onDaysChanged={daysChanged}
                        isLoading={isLoading}
                        showFilter={false}
                        onExportCSV={handleCSVExport}
                    />
                </Col>
            </Row>
            <Row id="actions-and-filter" gutter={[0, 16]}>
                <Col span={3}>
                    <Dropdown overlay={getBulkMenu()}>
                        <Button id="bulk-edit-button">
                            <Space>
                                Bulk Edit
                                <CaretDownFilled />
                            </Space>
                        </Button>
                    </Dropdown>
                </Col>
                <Col span={9}>
                    <Space>
                        <div>Find By...</div>
                        <Search
                            addonBefore={<Select
                                defaultValue={FindByKey.eBayItemId}
                                style={{ width: '94px' }}
                                options={[{ label: 'eBay ID', value: FindByKey.eBayItemId }, { label: 'SKU', value: FindByKey.SKU }]}
                                onChange={setFindByKey}
                            />}
                            allowClear
                            onSearch={handleFindBy}
                        />
                        {isAdminOrSeniorStaff && <NewListingModal />}
                    </Space>
                </Col>
                <Col span={24}>
                    <StaffCardsFilter detail={filterDetail} />
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Table
                        id="staff-card-table"
                        rowClassName={(rec, _) =>
                            recentUpdates.has(rec.cardId)
                                ? "recently-updated"
                                : ""
                        }
                        rowSelection={{
                            onChange: (keys, rows) => {
                                setSelectedCardIds(keys);
                                const sameOwnershipModel = allSameOwnershipModel(rows);
                                setCanBulkEditSelection(sameOwnershipModel);
                            }
                        }}
                        columns={columns}
                        dataSource={records}
                        rowKey={(r) => r.cardId}
                        onChange={tableChanged}
                        pagination={{
                            total: totalRecords,
                            current: page,
                            pageSize,
                            pageSizeOptions: PageSizeOptions,
                            showSizeChanger: true,
                            showQuickJumper: true,
                        }}
                        showSorterTooltip={false}
                        loading={isLoading}
                    />
                </Col>
            </Row>
            <BulkEditSelectionModal
                cardIds={selectedCardIds}
                affectedCount={selectedCardIds.length}
                visible={bulkSelectionModalVisible}
                silentAccounts={canBulkEditSelection && (records.find(r => r.cardId === selectedCardIds[0])?.ownersAreSilent ?? false)}
                multipleOwners={canBulkEditSelection && (records.find(r => r.cardId === selectedCardIds[0])?.canHaveMultipleOwners ?? false)}
                onSuccess={() =>
                    highlightAndRefresh(selectedCardIds as string[])
                }
                onClose={() => setBulkSelectionModalVisible(false)}
            />
            <BulkEditFilterModal
                pageRequest={{ page, filterBy, withinDays, sort }}
                affectedCount={totalRecords || 0}
                visible={bulkFilterModalVisible}
                silentAccounts={records[0]?.ownersAreSilent}
                multipleOwners={records[0]?.canHaveMultipleOwners}
                onSuccess={refetch}
                onClose={() => setBulkFilterModalVisible(false)}
            />
        </Box>
    );
};

export default Cards;
